// Refund / Cancellation Policy — mirrors the rules enforced by the
// /bookings/{id}/cancel and /bookings/{id}/reschedule backend endpoints so the
// customer-facing page never drifts from the actual code behaviour.
import { Link } from "react-router-dom";

const LAST_UPDATED = "16 June 2026";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-white/10 bg-black/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="font-serif text-2xl text-[#D4AF37]">Travelsate</Link>
          <Link to="/" className="text-white/60 hover:text-[#D4AF37] text-sm">← Home</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12" data-testid="refund-policy-page">
        <h1 className="font-serif text-4xl text-[#D4AF37] mb-2">Refund &amp; Cancellation Policy</h1>
        <p className="text-white/40 text-xs mb-10">Last updated: {LAST_UPDATED}</p>

        <Section title="Cancellation by you">
          You can cancel any confirmed booking from <strong>My Bookings → Cancel</strong>.
          Refund eligibility is calculated as a percentage of the amount you've
          paid so far (sum of upfront + any EMIs auto-debited):
          <table className="mt-4 w-full text-xs border border-white/10">
            <thead className="bg-white/[0.03]">
              <tr>
                <th className="px-3 py-2 text-left border-b border-white/10">Cancel before travel</th>
                <th className="px-3 py-2 text-right border-b border-white/10">Refund</th>
              </tr>
            </thead>
            <tbody className="text-white/70">
              <tr><td className="px-3 py-2 border-b border-white/10">≥ 60 days</td><td className="px-3 py-2 text-right border-b border-white/10">90%</td></tr>
              <tr><td className="px-3 py-2 border-b border-white/10">30 – 59 days</td><td className="px-3 py-2 text-right border-b border-white/10">70%</td></tr>
              <tr><td className="px-3 py-2 border-b border-white/10">15 – 29 days</td><td className="px-3 py-2 text-right border-b border-white/10">50%</td></tr>
              <tr><td className="px-3 py-2 border-b border-white/10">7 – 14 days</td><td className="px-3 py-2 text-right border-b border-white/10">25%</td></tr>
              <tr><td className="px-3 py-2">&lt; 7 days</td><td className="px-3 py-2 text-right">No refund</td></tr>
            </tbody>
          </table>
          <p className="mt-3 text-white/50 text-xs">
            EMI plans: future scheduled debits are cancelled with Razorpay immediately so no further auto-debits will occur.
          </p>
        </Section>

        <Section title="Reschedule">
          You may reschedule once per booking from <strong>My Bookings → Reschedule</strong>:
          <ul className="list-disc list-inside mt-2 space-y-1 text-white/70">
            <li>Free of charge if requested ≥ 30 days before original travel.</li>
            <li>₹2,500 reschedule fee + any fare/hotel difference if &lt; 30 days.</li>
            <li>New dates must respect the EMI pre-travel buffer (15 days domestic / 30 days international) so installments can complete.</li>
          </ul>
        </Section>

        <Section title="Pause EMI">
          Cash-flow tight this month? You can skip one upcoming EMI cycle per booking
          from <strong>My Bookings → Pause EMI for 1 cycle</strong>. The remaining
          installments continue on schedule from the next cycle.
        </Section>

        <Section title="Refund processing">
          <ul className="list-disc list-inside space-y-1 text-white/70">
            <li>Refunds are issued back to the original payment source via Razorpay.</li>
            <li>UPI / wallet: usually 3–5 business days.</li>
            <li>Credit / debit cards: 5–7 business days (bank-dependent).</li>
            <li>You'll receive an email confirmation with the Razorpay refund reference once initiated.</li>
          </ul>
        </Section>

        <Section title="Cancellation by us">
          In the rare event we cancel a booking (force majeure, supplier failure,
          fraud detection), you receive a 100% refund of all amounts paid,
          processed within 7 business days.
        </Section>

        <Section title="Disputes">
          Please write to <a href="mailto:admin@travelsate.in" className="text-[#D4AF37] underline">admin@travelsate.in</a> or call <a href="tel:+919972744308" className="text-[#D4AF37] underline">+91 99727 44308</a>
          {" "}before initiating a chargeback. We commit to a written response within 5 business days.
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="font-serif text-xl text-white mb-3">{title}</h2>
      <div className="text-white/70 text-sm leading-relaxed">{children}</div>
    </section>
  );
}
